import React, { useCallback, useEffect, useState } from "react";
import { MoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, Flex, Modal } from "antd";
import { toast } from "react-toastify";
import { PlusOutlined } from "@ant-design/icons";
import CustomLoader from "../../../common/UI/Loader";
import ENDPOINTS from "../../../../services/endpoint";
import apiInstance from "../../../../services/apiService";
import "./index.scss";
import { customLogger } from "../../../../utils/Logger";

interface PaymentCardProps {
  handleViewCard: () => void;
  setPaymentCardId: (id: string) => void;
  onCardsLoaded?: (hasCards: boolean) => void;
}

interface AddCardModalDetails {
  status: string;
  url: string;
  action: string;
}

interface PaymentCardData {
  cardId: string;
  brand: string;
  last4: string;
}

interface GetCardsResponse {
  litsCards?: PaymentCardData[];
}

interface AddCardResponse {
  data: AddCardModalDetails;
}

interface DeleteCardResponse {
  message?: string;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  return error instanceof Error ? error.message : fallback;
};

const PaymentCard: React.FC<PaymentCardProps> = ({
  handleViewCard,
  setPaymentCardId,
  onCardsLoaded,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [addCardLoading, setAddCardLoading] = useState<boolean>(false);
  const [cardData, setCardData] = useState<PaymentCardData[]>([]);
  const [isConfirmationModalOpen, setConfirmationModalOpen] =
    useState<boolean>(false);
  const [addCardModalOpen, setAddCardModalOpen] = useState<boolean>(false);
  const [addCardModalDetails, setAddCardModalDetails] =
    useState<AddCardModalDetails | null>(null);

  const getCardImage = (type: string) => {
    switch (type.toLowerCase()) {
      case "visa":
        return <img src="assets/images/card/vs.png" alt="Visa Card" />;
      case "master card":
        return <img src="assets/images/card/ms.png" alt="MasterCard" />;
      case "american_express":
      case "amex":
        return (
          <img
            src="/assets/images/card/amex.png"
            alt="American Express"
            style={{ transform: "scale(0.35)" }}
          />
        );
      case "diners":
      case "diners_club":
        return (
          <img
            src="assets/images/card/dinerc.png"
            alt="Diners Club"
            style={{ transform: "scale(0.125)" }}
          />
        );
      case "discover":
        return (
          <img
            src="assets/images/card/dis.png"
            alt="Discover"
            style={{ transform: "scale(0.125)" }}
          />
        );
      case "jcb":
        return (
          <img
            src="assets/images/card/jcb.png"
            alt="JCB"
            style={{ transform: "scale(0.33)" }}
          />
        );
      case "elo":
        return <img src="assets/images/card/elo.png" alt="Elo" />;
      default:
        return <img src="assets/images/card/ms.png" alt="Default Card" />;
    }
  };

  const getCardData = useCallback(async () => {
    const url = ENDPOINTS.GET_CARD;

    setLoading(true);
    try {
      const response = await apiInstance.get<GetCardsResponse>(url);
      const data = response.data.litsCards ?? [];
      setCardData(data);
      onCardsLoaded?.(data.length > 0);
    } catch (error: unknown) {
      customLogger(error);
      toast.error(
        getErrorMessage(error, "There was an error fetching Card information")
      );
      onCardsLoaded?.(false);
    } finally {
      setLoading(false);
    }
  }, [onCardsLoaded]);

  const handleAddCard = async () => {
    const url = ENDPOINTS.ADD_CARD;

    setAddCardLoading(true);
    try {
      const response = await apiInstance.get<AddCardResponse>(url);
      const data = response.data.data;
      setAddCardModalDetails(data);
      setAddCardModalOpen(true);
    } catch (error: unknown) {
      customLogger(error);
      toast.error(getErrorMessage(error, "An error occured. Please try again."));
    } finally {
      setAddCardLoading(false);
    }
  };

  const handleCardDelete = async () => {
    const selectedCard = cardData[0];

    if (!selectedCard) {
      return;
    }

    const url = ENDPOINTS.GET_CARD + "/" + selectedCard.cardId;

    setAddCardLoading(true);
    try {
      const response = await apiInstance.delete<DeleteCardResponse>(url);
      const data = response.data;
      toast.success(data.message || "Card has been deleted successfully");
      setConfirmationModalOpen(false);
    } catch (error: unknown) {
      customLogger(error);
      toast.error(getErrorMessage(error, "An error occured. Please try again."));
    } finally {
      setAddCardLoading(false);
    }
  };

  useEffect(() => {
    getCardData();
  }, [getCardData]);

  return (
    <div className="payment-section">
      {loading ? (
        <Flex className="w-full h-full" justify="center" align="center">
          <CustomLoader />
        </Flex>
      ) : cardData?.length > 0 ? (
        <div className="payment-card">
          <div className="card-image">{getCardImage(cardData[0]?.brand)}</div>

          <div className="card-details">
            <h2 className="card-name">{cardData[0]?.brand}</h2>
            <p className="card-number">**** **** **** {cardData[0].last4}</p>
          </div>

          <Dropdown
          overlayClassName="custom-dropdown"
            menu={{
              items: [
                {
                  key: "1",
                  label: (
                    <span
                      onClick={() => {
                        setPaymentCardId(cardData[0]?.cardId);
                        handleViewCard();
                      }}
                    >
                      View
                    </span>
                  ),
                },
                {
                  key: "2",
                  label: (
                    <span onClick={() => setConfirmationModalOpen(true)}>
                      Delete
                    </span>
                  ),
                },
              ],
            }}
          >
            <Button type="text" className="more-btn" icon={<MoreOutlined />} />
          </Dropdown>
        </div>
      ) : (
        <Button
          type="text"
          className="add-card-button"
          icon={<PlusOutlined />}
          onClick={handleAddCard}
          loading={addCardLoading}
        >
          Add New Card
        </Button>
      )}

      <Modal
        open={addCardModalOpen}
        onCancel={() => setAddCardModalOpen(false)}
        afterClose={getCardData}
        footer={null}
      >
        <iframe
          src={addCardModalDetails?.url}
          width="100%"
          height="660px"
          title="Add payment card"
          style={{ border: "none" }}
          allowFullScreen
        />
      </Modal>

      <Modal
        title="Confirm Deletion"
        className="custom-modal"
        centered
        open={isConfirmationModalOpen}
        onOk={handleCardDelete}
        onCancel={() => setConfirmationModalOpen(false)}
        afterClose={getCardData}
        okText="Yes"
        cancelText="No"
        okButtonProps={{
          className: 'modal-ok-btn',
          loading: addCardLoading,
        }}
        cancelButtonProps={{
          className: 'modal-cancel-btn',
          disabled: addCardLoading,
          type: "text",
        }}
      >
        <p>Are you sure you want to delete this Card?</p>
      </Modal>
    </div>
  );
};

export default PaymentCard;
